import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import MobiusPage from "@insite/mobius/Page";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import osnGetQuote from "@WhiteCap/Handlers/OsnQuotes/GetQuoteHandler";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    osnGetQuote,
    loadCurrentCart,
};

const mapStateToProps = (state: CustomApplicationState) => {
    const session = state.context.session;
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    return {
        isLoading: state.WhiteCap.osnCreateOrEditQuote.isLoading,
        data: state.WhiteCap.osnCreateOrEditQuote.quoteData,
        session,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        state,
        osnCreateQuotePageUrl: getPageLinkByPageType(state, "OsnCreateQuotePage")?.url,
        isAuthenticated,
        location: getLocation(state),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("AccountManager") !== -1,
    };
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

export interface SubscriptionsDashboardPageStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    errorMessage?: TypographyProps;
}

export const subscriptionsDashboardPageStyles: SubscriptionsDashboardPageStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    errorMessage: {
        variant: "h2",
        weight: 500,
        css: css`
            margin-top: 40px;
            text-align: center;
        `,
    },
};

const styles = subscriptionsDashboardPageStyles;

const Page = ({
    id,
    osnGetQuote,
    isLoading,
    session,
    history,
    signInPageUrl,
    isAuthenticated,
    location,
    loadCurrentCart,
    canViewQuotes,
}: Props) => {
    const { isInShell } = useShellContext();
    const dispatch = useDispatch();
    const returnUrl = location.pathname + location.search;
    const notAuthorizedMessage = siteMessage("OSN_Quotes_Module_User_Not_Authorized_Message");

    if (!isAuthenticated && !isInShell && signInPageUrl) {
        history.push(`${signInPageUrl}?returnUrl=${returnUrl}`);
    }

    const billToId = session.billToId;
    const shipToId = session.billToId;
    const [error, setError] = useState("");
    const parsedQuery = parseQueryString<{
        quote?: string;
    }>(location.search);
    const quote = parsedQuery.quote || "";

    useEffect(() => {
        if (isInShell) {
            return;
        }

        osnGetQuote({
            quoteNumber: quote,
            onError: (message: string) => {
                setError(message);
            },
            onSuccess: (quoteId?: string) => {
                const emptyGuid = "00000000-0000-0000-0000-000000000000";
                if (quoteId && quoteId !== emptyGuid) {
                    history.push(`${location.pathname}?quote=${quoteId}`);
                    loadCurrentCart({ shouldLoadFullCart: true });
                }
            },
        });
    }, [billToId, shipToId]);

    useEffect(() => {
        return () => {
            dispatch({
                type: "Custom/OsnQuote/ReinitializeState",
            });
        };
    }, []);

    if (!canViewQuotes) {
        return <Typography {...styles.errorMessage}>{notAuthorizedMessage}</Typography>;
    }

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (error) {
        return <Typography {...styles.errorMessage}>{error}</Typography>;
    }

    return (
        <MobiusPage>
            <Zone contentId={id} zoneName="Content"></Zone>
        </MobiusPage>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(Page)),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;
